/**
 * Export resolution presets and normalization helpers.
 */

export type GifResolutionPreset =
  | 'source'
  | '240p'
  | '360p'
  | '480p'
  | '720p'
  | 'custom'

export type FitMode = 'contain' | 'cover' | 'stretch'

export interface ResolvedExportDimensions {
  width: number
  height: number
  fitMode: FitMode
}

interface ResolutionPresetMapEntry {
  width: number
  height: number
}

interface NormalizeResolutionOptions<TPreset extends string> {
  preset?: TPreset
  width?: number
  height?: number
  scale?: number
  fitMode?: FitMode
  sourceWidth?: number
  sourceHeight?: number
}

const GIF_PRESET_DIMENSIONS: Record<Exclude<GifResolutionPreset, 'source' | 'custom'>, ResolutionPresetMapEntry> = {
  '240p': { width: 426, height: 240 },
  '360p': { width: 640, height: 360 },
  '480p': { width: 854, height: 480 },
  '720p': { width: 1280, height: 720 },
}

export const GIF_RESOLUTION_PRESETS = GIF_PRESET_DIMENSIONS

export function resolveGifResolutionPreset(
  preset: Exclude<GifResolutionPreset, 'source' | 'custom'>
): ResolutionPresetMapEntry {
  return GIF_PRESET_DIMENSIONS[preset]
}

function sanitizeDimension(value: number | undefined, fallback: number): number {
  const rounded = Math.round(value || fallback)
  return Math.max(1, rounded)
}

function normalizeResolution<TPreset extends string>(
  options: NormalizeResolutionOptions<TPreset>,
  presets: Record<string, ResolutionPresetMapEntry>,
  fallback: ResolutionPresetMapEntry
): ResolvedExportDimensions {
  const fitMode = options.fitMode ?? 'contain'
  const scale = options.scale ?? 1
  const source = {
    width: sanitizeDimension(options.sourceWidth, fallback.width),
    height: sanitizeDimension(options.sourceHeight, fallback.height),
  }

  if (options.preset === 'custom') {
    return {
      width: sanitizeDimension(options.width, source.width * scale),
      height: sanitizeDimension(options.height, source.height * scale),
      fitMode,
    }
  }

  if (!options.preset || options.preset === 'source') {
    return {
      width: sanitizeDimension(source.width * scale, fallback.width),
      height: sanitizeDimension(source.height * scale, fallback.height),
      fitMode,
    }
  }

  const presetDimensions = presets[options.preset]
  if (!presetDimensions) {
    return {
      width: source.width,
      height: source.height,
      fitMode,
    }
  }

  return {
    width: sanitizeDimension(presetDimensions.width, fallback.width),
    height: sanitizeDimension(presetDimensions.height, fallback.height),
    fitMode,
  }
}

export function normalizeGifResolutionOptions(
  options: NormalizeResolutionOptions<GifResolutionPreset>
): ResolvedExportDimensions {
  return normalizeResolution(
    options,
    GIF_PRESET_DIMENSIONS,
    { width: 426, height: 240 }
  )
}
